import type { QueryClient, QueryKey } from "@tanstack/react-query";

type ConnectionState = "open" | "error";

export const SSE_FALLBACK_POLL_MS = 30_000;
export const SSE_SAFETY_POLL_MS = 5 * 60_000;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 60_000;
// EventSource.CLOSED; read as a literal so environments without the global still compile the check.
const EVENT_SOURCE_CLOSED = 2;

interface Listener {
  queryKey: QueryKey;
  exact: boolean;
  onConnectionChange?: (state: ConnectionState) => void;
  shouldInvalidate?: (event: Event) => boolean;
}

interface Subscription {
  listeners: Set<Listener>;
  connectionState?: ConnectionState;
  enqueue: (listener: Listener) => void;
  cancel: () => void;
}

const subscriptions = new WeakMap<QueryClient, Map<string, Subscription>>();

interface SubscribeOptions {
  id: string;
  url: string;
  eventNames: readonly string[];
  queryKey: QueryKey;
  /** Prefix-match `queryKey` so one long-lived listener covers every page/filter variant. */
  exact?: boolean;
  onConnectionChange?: (state: ConnectionState) => void;
  shouldInvalidate?: (event: Event) => boolean;
  coalesceMs?: number;
}

/**
 * One EventSource per QueryClient + logical stream, with ref-counted query
 * invalidation. SSE carries hints only; every consumer re-reads canonical GETs.
 */
export function subscribeToSseInvalidation(client: QueryClient, options: SubscribeOptions) {
  if (typeof EventSource === "undefined") {
    options.onConnectionChange?.("error");
    return () => {};
  }
  let map = subscriptions.get(client);
  if (!map) {
    map = new Map();
    subscriptions.set(client, map);
  }
  let subscription = map.get(options.id);
  if (!subscription) {
    const listeners = new Set<Listener>();
    const pending = new Set<Listener>();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let reconnectDelay = RECONNECT_BASE_MS;
    let source: EventSource;
    const arm = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = undefined;
        const invalidated = new Set<string>();
        for (const listener of pending) {
          if (!listeners.has(listener)) continue;
          const key = JSON.stringify([listener.queryKey, listener.exact]);
          if (invalidated.has(key)) continue;
          invalidated.add(key);
          void client.invalidateQueries({ queryKey: listener.queryKey, exact: listener.exact });
        }
        pending.clear();
      }, options.coalesceMs ?? 200);
    };
    const enqueue = (listener: Listener) => {
      pending.add(listener);
      arm();
    };
    const schedule = (event?: Event) => {
      for (const listener of listeners) {
        if (!event || !listener.shouldInvalidate || listener.shouldInvalidate(event)) {
          pending.add(listener);
        }
      }
      arm();
    };
    const opened = () => {
      reconnectDelay = RECONNECT_BASE_MS;
      if (subscription) subscription.connectionState = "open";
      for (const listener of listeners) listener.onConnectionChange?.("open");
      schedule();
    };
    const failed = () => {
      if (subscription) subscription.connectionState = "error";
      for (const listener of listeners) listener.onConnectionChange?.("error");
      // The browser retries transient drops itself, but gives up for good once a reconnect
      // receives a non-200 (e.g. 401 before the access cookie is refreshed). Recreate the
      // source with backoff so the stream recovers instead of polling for the whole session.
      if (source.readyState === EVENT_SOURCE_CLOSED && !reconnectTimer) {
        detach();
        reconnectTimer = setTimeout(() => {
          reconnectTimer = undefined;
          connect();
        }, reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
      }
    };
    const connect = () => {
      source = new EventSource(options.url);
      source.addEventListener("open", opened);
      source.addEventListener("error", failed);
      for (const eventName of options.eventNames) source.addEventListener(eventName, schedule);
    };
    const detach = () => {
      source.removeEventListener("open", opened);
      source.removeEventListener("error", failed);
      for (const eventName of options.eventNames) source.removeEventListener(eventName, schedule);
      source.close();
    };
    connect();
    subscription = {
      listeners,
      enqueue,
      cancel: () => {
        if (timer) clearTimeout(timer);
        if (reconnectTimer) clearTimeout(reconnectTimer);
        detach();
      },
    };
    map.set(options.id, subscription);
  }
  const listener: Listener = {
    queryKey: options.queryKey,
    exact: options.exact ?? true,
    onConnectionChange: options.onConnectionChange,
    shouldInvalidate: options.shouldInvalidate,
  };
  subscription.listeners.add(listener);
  if (subscription.connectionState) {
    listener.onConnectionChange?.(subscription.connectionState);
  }
  // A run may have completed between the caller's canonical GET and listener
  // registration on an already-open shared stream.
  subscription.enqueue(listener);
  return () => {
    subscription.listeners.delete(listener);
    if (subscription.listeners.size === 0) {
      subscription.cancel();
      map.delete(options.id);
    }
  };
}
